import { useCart } from "./StoreLayout";
import { formatIDR, unitPrice } from "@/lib/format";
import { Trash2, ChevronLeft, ShoppingBag, Heart } from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

const COLORS_V = ["Pink", "White", "Blue", "Black"];
const SIZES_V = ["S", "M", "L", "XL"];

function VariantEditor({ current, onSave, onClose }) {
  const [c, s] = (current || "Pink, M").split(", ");
  const [color, setColor] = useState(COLORS_V.includes(c) ? c : "Pink");
  const [size, setSize] = useState(SIZES_V.includes(s) ? s : "M");
  return (
    <div className="mt-2 bg-blush/60 rounded-xl p-3 space-y-2">
      <div className="flex gap-1.5 flex-wrap">
        {COLORS_V.map((cl) => (
          <button key={cl} data-testid={`edit-color-${cl}`} onClick={() => setColor(cl)}
            className={`px-3 h-7 rounded-full text-[11px] border ${color === cl ? "bg-brand text-white border-brand" : "bg-white border-blush-soft"}`}>{cl}</button>
        ))}
      </div>
      <div className="flex gap-1.5">
        {SIZES_V.map((sz) => (
          <button key={sz} data-testid={`edit-size-${sz}`} onClick={() => setSize(sz)}
            className={`w-9 h-7 rounded-full text-[11px] border font-medium ${size === sz ? "bg-brand text-white border-brand" : "bg-white border-blush-soft"}`}>{sz}</button>
        ))}
      </div>
      <div className="flex gap-2">
        <button onClick={onClose} className="flex-1 h-8 rounded-full border border-blush-soft text-[11px] bg-white">Batal</button>
        <button data-testid="edit-variant-save" onClick={() => onSave(`${color}, ${size}`)} className="flex-1 h-8 rounded-full bg-brand text-white text-[11px] font-semibold">Simpan Variasi</button>
      </div>
    </div>
  );
}

export default function StoreCart() {
  const { cart, removeFromCart, updateQty, updateVariant, toggleWish } = useCart();
  const nav = useNavigate();
  const [selected, setSelected] = useState(() => new Set(cart.map((x) => x.key)));
  const [editKey, setEditKey] = useState(null);

  const toggle = (key) => setSelected((s) => {
    const n = new Set(s);
    n.has(key) ? n.delete(key) : n.add(key);
    return n;
  });
  const toggleAll = () => setSelected((s) => s.size === cart.length ? new Set() : new Set(cart.map((x) => x.key)));

  const selItems = cart.filter((x) => selected.has(x.key));
  const subtotal = selItems.reduce((s, x) => s + unitPrice(x, x.qty) * x.qty, 0);
  const freeShipRemain = Math.max(0, 150000 - subtotal);

  if (cart.length === 0) {
    return (
      <div className="text-center py-20">
        <ShoppingBag className="w-14 h-14 text-cocoa-muted mx-auto mb-3" />
        <div className="font-serif-fashion text-2xl mb-2 text-cocoa">Keranjang Kosong</div>
        <div className="text-sm text-cocoa-muted mb-5">Yuk temukan koleksi Kiyana kesukaanmu.</div>
        <button onClick={() => nav("/store")} className="px-6 h-11 rounded-full bg-brand text-white text-sm font-semibold">Mulai Belanja</button>
      </div>
    );
  }

  return (
    <div className="space-y-3 animate-fade-up pb-24">
      <div className="flex items-center justify-between">
        <button onClick={() => nav(-1)} className="w-9 h-9 rounded-full bg-white border border-blush-soft flex items-center justify-center">
          <ChevronLeft className="w-5 h-5" />
        </button>
        <h1 className="font-semibold">Keranjang Belanja</h1>
        <button className="text-xs text-brand font-medium w-9">Edit</button>
      </div>

      {freeShipRemain > 0 ? (
        <div className="p-3 bg-blush rounded-xl text-xs text-cocoa">
          Belanja <b className="text-brand">{formatIDR(freeShipRemain)}</b> lagi untuk gratis ongkir!
        </div>
      ) : (
        <div className="p-3 bg-emerald-50 rounded-xl text-xs text-emerald-700">Yeay! Kamu dapat <b>Gratis Ongkir</b> 🎉</div>
      )}

      {cart.map((it) => (
        <div key={it.key} className="flex gap-3 bg-white rounded-2xl p-3 border border-blush-soft items-center">
          <button
            data-testid={`cart-check-${it.key}`}
            onClick={() => toggle(it.key)}
            className={`w-5 h-5 rounded-full border-2 flex-shrink-0 flex items-center justify-center ${selected.has(it.key) ? "bg-brand border-brand" : "border-blush-soft bg-white"}`}
          >
            {selected.has(it.key) && <span className="text-white text-[10px]">✓</span>}
          </button>
          <img src={it.image} alt={it.name} className="w-16 h-16 object-cover rounded-xl" />
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium line-clamp-1">{it.name}</div>
            <div className="text-[11px] text-cocoa-muted">
              {it.variant || "All Size"}{" "}
              <button data-testid={`cart-edit-${it.key}`} onClick={() => setEditKey(editKey === it.key ? null : it.key)} className="text-brand font-medium">Ubah</button>
            </div>
            {editKey === it.key && (
              <VariantEditor current={it.variant} onClose={() => setEditKey(null)} onSave={(v) => { updateVariant(it.key, v); setEditKey(null); toast.success("Variasi diubah"); }} />
            )}
            <div className="flex items-center justify-between mt-1.5">
              <div className="text-brand font-bold text-sm">{formatIDR(unitPrice(it, it.qty))}</div>
              {unitPrice(it, it.qty) < (it.promo_price ?? it.price) && <div className="text-[9px] text-emerald-600 font-semibold">Harga grosir aktif</div>}
              <div className="flex items-center gap-2">
                <button
                  data-testid={`cart-wish-${it.key}`}
                  onClick={() => { toggleWish({ id: it.id, name: it.name, price: it.price, image: it.image }); removeFromCart(it.key); toast.success("Dipindah ke wishlist"); }}
                  className="text-cocoa-muted"
                >
                  <Heart className="w-4 h-4" />
                </button>
                <button data-testid={`cart-del-${it.key}`} onClick={() => removeFromCart(it.key)} className="text-cocoa-muted">
                  <Trash2 className="w-4 h-4" />
                </button>
                <div className="flex items-center border border-blush-soft rounded-full bg-canvas-soft">
                  <button onClick={() => updateQty(it.key, it.qty - 1)} className="w-7 h-7">−</button>
                  <div className="w-7 text-center text-xs font-medium">{it.qty}</div>
                  <button data-testid={`cart-plus-${it.key}`} onClick={() => updateQty(it.key, it.qty + 1)} className="w-7 h-7">+</button>
                </div>
              </div>
            </div>
          </div>
        </div>
      ))}

      {/* Footer */}
      <div className="fixed bottom-[57px] inset-x-0 z-40 bg-white border-t border-blush-soft">
        <div className="max-w-2xl mx-auto px-4 py-3 flex items-center gap-3">
          <button data-testid="cart-select-all" onClick={toggleAll} className="flex items-center gap-1.5 text-xs text-cocoa">
            <span className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${selected.size === cart.length ? "bg-brand border-brand" : "border-blush-soft"}`}>
              {selected.size === cart.length && <span className="text-white text-[10px]">✓</span>}
            </span>
            Pilih Semua
          </button>
          <div className="flex-1 text-right">
            <div className="text-[10px] text-cocoa-muted">Total ({selItems.length} produk)</div>
            <div className="text-brand font-bold" data-testid="cart-total">{formatIDR(subtotal)}</div>
          </div>
          <button
            data-testid="cart-checkout-btn"
            onClick={() => nav("/store/checkout")}
            disabled={selItems.length === 0}
            className="h-11 px-7 rounded-full bg-brand text-white text-sm font-semibold disabled:opacity-40"
          >
            Checkout
          </button>
        </div>
      </div>
    </div>
  );
}
